import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment';
import { MessageService } from 'primeng/api';
import { Observable, map, withLatestFrom } from 'rxjs';
import { Gender, InsuranceFormValues, InsuranceType, RelationshipToInsured, UsState } from 'src/app/core/models';
import { LookupState } from 'src/app/core/store/lookup/lookup.state';
import { CreatePatientInsurance } from 'src/app/core/store/patient-insurances/patient-insurances.actions';
import { PatientInsurancesState } from 'src/app/core/store/patient-insurances/patient-insurances.state';
import { ApiStoreData } from 'src/app/core/store/utils';
import {
  maxDateValidator,
  minDateValidator,
  phoneValidator,
  validateAllFormFields,
  zipCodeValidator,
} from 'src/app/core/utils/validators';
import { FormFieldOption } from '../form-field/form-field.component';

// TODO:
// Replace tap/subscribe with subscribe with pipe first
// Replace form builder with direct object construction
@Component({
  selector: 'app-insurance-form',
  templateUrl: './insurance-form.component.html',
  styleUrls: ['./insurance-form.component.scss'],
})
export class InsuranceFormComponent implements OnInit {
  @Input() title!: string;
  @Input() visible!: boolean;
  @Input() includeInsuranceType: boolean = false;
  @Input() initialValues: InsuranceFormValues | null = null;
  @Input() submitHandler?: (values: InsuranceFormValues) => void;
  @Input() successMessageServiceKey?: string;

  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Select(LookupState.relationshipsToInsured) relationshipsToInsured$!: Observable<RelationshipToInsured[]>;
  @Select(LookupState.usStates) usStates$!: Observable<UsState[]>;
  @Select(PatientInsurancesState.createInsuranceState) createInsuranceState$!: Observable<ApiStoreData<null>>;

  genderOptions: FormFieldOption[] = [
    { value: Gender.Male, label: 'Male' },
    { value: Gender.Female, label: 'Female' },
  ];

  insuranceTypeOptions: FormFieldOption[] = [
    { value: InsuranceType.Primary, label: 'Primary' },
    { value: InsuranceType.Secondary, label: 'Secondary' },
    { value: InsuranceType.Tertiary, label: 'Tertiary' },
  ];

  minDobOfInsured = moment('1900-01-01').startOf('day');
  maxDobOfInsured = moment().startOf('day');

  insuranceForm = this.formBuilder.group({
    insuranceType: [null],
    insurancePlan: [null, [Validators.required, Validators.maxLength(30)]],
    insuranceNumber: [null, [Validators.required, Validators.maxLength(15)]],
    groupNumber: [null, Validators.maxLength(20)],
    insurancePhone: [null, [Validators.required, phoneValidator()]],
    relationshipToInsured: [null, Validators.required],
    policyHolderInformation: this.formBuilder.group({
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      dob: [
        null,
        [Validators.required, minDateValidator(this.minDobOfInsured), maxDateValidator(this.maxDobOfInsured)],
      ],
      gender: [null, Validators.required],
      phoneNumber: [null, [Validators.required, phoneValidator()]],
      line1: [null, [Validators.required, Validators.maxLength(255)]],
      city: [null, [Validators.required, Validators.maxLength(25)]],
      state: [null, Validators.required],
      zipCode: [null, [Validators.required, zipCodeValidator()]],
    }),
  });

  createInsuranceError?: string;

  relationshipToInsuredOptions$ = this.relationshipsToInsured$.pipe(
    map((relationships) =>
      relationships.map((r) => ({
        label: r.name,
        value: r.value,
      }))
    )
  );

  usStateOptions$ = this.usStates$.pipe(
    map((usStates) =>
      usStates.map((s) => ({
        label: s.name,
        value: s.value,
      }))
    )
  );

  constructor(private formBuilder: FormBuilder, private store: Store, private messageService: MessageService) {}

  ngOnInit(): void {
    if (this.includeInsuranceType) {
      this.insuranceForm.get('insuranceType')?.addValidators(Validators.required);
    }

    if (this.initialValues) {
      this.insuranceForm.setValue(this.initialValues);
    }
  }

  onCancelInsuranceForm() {
    if (this.initialValues) {
      this.insuranceForm.setValue(this.initialValues);
      this.insuranceForm.markAsPristine();
      this.insuranceForm.markAsUntouched();
    } else {
      this.insuranceForm.reset();
    }
    this.closeModal();
  }

  onSubmitInsuranceForm() {
    if (this.insuranceForm.valid) {
      if (this.submitHandler) {
        this.submitHandler(this.insuranceForm.value);
        this.initialValues = this.insuranceForm.value;
        this.closeModal();
      } else {
        this.submit(this.insuranceForm.value);
      }
    } else {
      validateAllFormFields(this.insuranceForm);
    }
  }

  submit(formValues: InsuranceFormValues) {
    this.store
      .dispatch(new CreatePatientInsurance(formValues))
      .pipe(withLatestFrom(this.createInsuranceState$))
      .subscribe(([_, createInsuranceState]) => {
        if (createInsuranceState.isFinished && createInsuranceState.error) {
          this.createInsuranceError = createInsuranceState.error.uiMessage;
        } else {
          if (this.successMessageServiceKey) {
            this.messageService.clear(this.successMessageServiceKey);

            this.messageService.add({
              key: this.successMessageServiceKey,
              severity: 'success',
              detail:
                'Thank you for updating your insurance information. We will update your account and confirm coverage with your insurance.',
            });
          }

          this.closeModal();
          this.insuranceForm.reset();
        }
      });
  }

  asFormControl(a: AbstractControl) {
    return a as FormControl;
  }

  closeModal() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
  }
}
